import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type Assignment = Schemas['AssignmentDto'];
export type AssignmentRequest = Schemas['AssignmentRequest'];
export type TeamRow = Schemas['TeamRowDto'];
export type DayLoad = Schemas['DayLoadDto'];
export type ProjectRow = Schemas['ProjectRowDto'];
export type Milestone = Schemas['MilestoneDto'];
export type AssignmentReportRow = Schemas['AssignmentReportRow'];

const key = (ws: string) => ['workspace', ws, 'schedule'] as const;
const wsPath = (ws: string) => ({ params: { path: { workspaceId: ws } } });
const idPath = (ws: string, id: string) => ({ params: { path: { workspaceId: ws, id } } });

export function useScheduleTeam(ws: string, from: string, to: string) {
  return useQuery({
    queryKey: [...key(ws), 'team', from, to],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/schedule/team', { params: { path: { workspaceId: ws }, query: { from, to } } })),
  });
}

export function useScheduleProjects(ws: string, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'projects', from, to],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/schedule/projects', { params: { path: { workspaceId: ws }, query: { from, to } } })),
    enabled,
  });
}

export function useMySchedule(ws: string, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'me', from, to],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/schedule/me', { params: { path: { workspaceId: ws }, query: { from, to } } })),
    enabled,
    retry: false,
  });
}

export function useAssignmentReport(ws: string, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['workspace', ws, 'reports', 'assignments', from, to],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/reports/assignments', { params: { path: { workspaceId: ws }, query: { from, to } } })),
    enabled,
  });
}

function useInvalidate(ws: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: key(ws) });
    void qc.invalidateQueries({ queryKey: ['workspace', ws, 'reports', 'assignments'] });
  };
}

export function useCreateAssignment(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: (body: AssignmentRequest) => unwrap(api.POST('/api/v1/workspaces/{workspaceId}/schedule/assignments', { ...wsPath(ws), body })),
    onSuccess: invalidate,
  });
}

export function useUpdateAssignment(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: ({ id, ...body }: AssignmentRequest & { id: string }) =>
      unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/schedule/assignments/{id}', { ...idPath(ws, id), body })),
    onSuccess: invalidate,
  });
}

export function useDeleteAssignment(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: ({ id, series }: { id: string; series?: boolean }) =>
      unwrap(
        api.DELETE('/api/v1/workspaces/{workspaceId}/schedule/assignments/{id}', {
          params: { path: { workspaceId: ws, id }, query: { series: !!series } },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useCopyAssignment(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['CopyRequest'] & { id: string }) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/schedule/assignments/{id}/copy', { ...idPath(ws, id), body })),
    onSuccess: invalidate,
  });
}

export function useSplitAssignment(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/schedule/assignments/{id}/split', { ...idPath(ws, id), body: { date } })),
    onSuccess: invalidate,
  });
}

export function usePublishSchedule(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: (body: Schemas['PublishRequest']) => unwrap(api.POST('/api/v1/workspaces/{workspaceId}/schedule/publish', { ...wsPath(ws), body })),
    onSuccess: invalidate,
  });
}

export function useSaveMilestone(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['MilestoneRequest'] & { id?: string }) =>
      id
        ? unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/schedule/milestones/{id}', { ...idPath(ws, id), body }))
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/schedule/milestones', { ...wsPath(ws), body })),
    onSuccess: invalidate,
  });
}

export function useDeleteMilestone(ws: string) {
  const invalidate = useInvalidate(ws);
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/schedule/milestones/{id}', idPath(ws, id))),
    onSuccess: invalidate,
  });
}
